import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index"; // Critical: Load immediately
// BackgroundCanvas is now lazy loaded
const BackgroundCanvas = lazy(() => import("./components/BackgroundCanvas"));

// Load critical path pages immediately to improve FCP and ensure stable hydration
import Blog from "./pages/Blog";
import FreeTools from "./pages/FreeTools";
import Consulting from "./pages/Consulting";
import Perspectives from "./pages/Perspectives";
import About from "./pages/About";
import NotFound from "./pages/NotFound";

// Lazy load larger or less critical pages
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LeadershipRoles = lazy(() => import("./pages/LeadershipRoles")); // Turbo auto-added
const RolesPage = lazy(() => import("./pages/leadership/RolesPage"));
const ModelPage = lazy(() => import("./pages/leadership/ModelPage"));
const AboutPage = lazy(() => import("./pages/leadership/AboutPage"));
const InterviewPage = lazy(() => import("./pages/leadership/InterviewPage"));
const StartupsPage = lazy(() => import("./pages/leadership/StartupsPage"));
const AIToolEvaluationPromptGenerator = lazy(() => import("./pages/AIToolEvaluationPromptGenerator"));
const Impressum = lazy(() => import("./pages/Impressum"));

// Import blog loader to register all posts
import "./content/blogLoader";

const queryClient = new QueryClient();

import { TransitionProvider, useTransition } from "@/context/TransitionContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { useEffect } from "react";
import Menu from "@/components/Menu";
import FPSCounter from "@/components/FPSCounter";
import SkipLink from "@/components/SkipLink";

const RoutesContent = () => {
  const { isFrozen } = useTransition();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle redirect from 404.html
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');

    if (redirect && redirect !== '/') {
      // Remove the redirect parameter from the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('redirect');
      window.history.replaceState({}, '', newUrl.toString());

      // Navigate to the intended path
      navigate(redirect, { replace: true });
    }
  }, [navigate]);

  // Disable particles on blog post pages for performance (they're obscured by black background anyway)
  // Also disable for the complex Leadership Roles animation to save compute
  const shouldShowParticles = !location.pathname.startsWith('/blog/') && !location.pathname.startsWith('/tools/leadership_roles');

  return (
    <>
      <SkipLink />
      {shouldShowParticles && (
        <div
          className="fixed inset-0 z-[-1]"
        >
          <Suspense fallback={null}>
            <BackgroundCanvas frozen={isFrozen} />
          </Suspense>
        </div>
      )}
      <Menu />
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/tools" element={<FreeTools />} />
          <Route path="/tools/leadership_roles" element={<LeadershipRoles />} />
          <Route path="/tools/leadership_roles/roles" element={<RolesPage />} />
          <Route path="/tools/leadership_roles/model" element={<ModelPage />} />
          <Route path="/tools/leadership_roles/about" element={<AboutPage />} />
          <Route path="/tools/leadership_roles/interview" element={<InterviewPage />} />
          <Route path="/tools/leadership_roles/startups" element={<StartupsPage />} />

          <Route path="/tools/ai-tool-evaluation-prompt-generator" element={<AIToolEvaluationPromptGenerator />} />
          <Route path="/case-studies" element={<Consulting />} />
          <Route path="/perspectives" element={<Perspectives />} />
          <Route path="/startups" element={<Navigate to="/perspectives" replace />} />

          <Route path="/about" element={<About />} />
          <Route path="/impressum" element={<Impressum />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const AppContent = () => {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RoutesContent />
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {import.meta.env.DEV && <FPSCounter />}
      <LanguageProvider>
        <TransitionProvider>
          <AppContent />
        </TransitionProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
